import { Box } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import React from "react";

interface Loader2DProps {
  cardSize: number;
}

const fadingAnimation = keyframes({
  "0%": { opacity: 0.3 },
  "8%": { opacity: 0.3 },
  "16%": { opacity: 1 },
  "40%": { opacity: 1 },
  "80%": { opacity: 0.3 },
  "100%": { opacity: 0.3 },
});

const skewShadowAnimation = keyframes({
  "0%": { transform: "skewX(630deg)", opacity: 1 },
  "30%": { transform: "skewX(570deg)", opacity: 1 },
  "100%": { transform: "skewX(450deg)", opacity: 0 },
});

const sunriseAnimation = keyframes({
  "0%": { transform: "rotate(-80deg)" },
  "30%": { transform: "rotate(-20deg)" },
  "100%": { transform: "rotate(70deg)" },
});

const Frame = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cardSize",
})<Loader2DProps>(({ cardSize }) => ({
  position: "relative",
  width: `${cardSize / 4}px`,
  height: `${cardSize / 4}px`,
  borderRadius: `${cardSize * 0.05}px`,
  overflow: "hidden",
  border: `${cardSize * 0.01}px solid white`,
  animation: `${fadingAnimation} 4s ease-in-out infinite`,
}));

const Sky = styled(Box)({
  height: "100%",
  position: "relative",
});

const Sun = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cardSize",
})<Loader2DProps>(({ cardSize }) => ({
  position: "absolute",
  width: `${cardSize / 18}px`,
  height: `${cardSize / 18}px`,
  backgroundColor: "#fff",
  borderRadius: "50%",
  top: "10%",
  left: "50%",
  transformOrigin: "50% 400%",
  animation: `${sunriseAnimation} 4s cubic-bezier(0.4, 0, 0.49, 1) infinite`,
}));

const MountainWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "cardSize",
})<Loader2DProps>(({ cardSize }) => ({
  position: "absolute",
  bottom: `-${cardSize / 110}px`,
  left: "50%",
  width: `${cardSize / 4}px`,
  transform: "translateX(-50%)",
  padding: `${cardSize / 40}px`,
}));

const MOUNTAIN_PATH =
  "M57.3448 43.6781C60.2672 43.6781 62.7025 41.2428 62.7025 38.3204C62.7025 37.2489 62.4103 36.2748 61.9232 35.3981L40.7848 1.69336C40.2003 0.71924 39.0314 0.0373535 37.765 0.0373535C36.5961 0.0373535 35.4271 0.71924 34.8426 1.69336L22.5687 21.2732L17.8929 13.5777C17.4059 12.9932 16.724 12.5061 15.9447 12.5061C15.2628 12.5061 14.4835 12.9932 14.0939 13.5777L1.23547 34.7162C0.748413 35.5929 0.358765 36.6644 0.358765 37.7359C0.358765 41.048 3.0863 43.6781 6.39831 43.6781H57.3448Z";

const Mountain: React.FC<Loader2DProps> = ({ cardSize }) => (
  <MountainWrapper cardSize={cardSize}>
    <svg width="100%" viewBox="0 0 63 44">
      <path d={MOUNTAIN_PATH} fill="#fff" />

      <defs>
        <clipPath id="mountain-clip">
          <path d={MOUNTAIN_PATH} />
        </clipPath>
      </defs>
      <g clipPath="url(#mountain-clip)">
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="#191a231c"
          style={{
            animation: `${skewShadowAnimation} 4s ease-in-out infinite`,
          }}
        />
      </g>
    </svg>
  </MountainWrapper>
);

export const Loader2D: React.FC<Loader2DProps> = ({ cardSize }) => {
  return (
    <Frame cardSize={cardSize}>
      <Sky>
        <Sun cardSize={cardSize} />
        <Mountain cardSize={cardSize} />
      </Sky>
    </Frame>
  );
};
